import React, { useState, useEffect } from 'react';
import { IconCalendar, IconMapPin, IconTrophy, IconMedal, IconArrowNarrowRight, IconChartBar, IconCrown } from '@tabler/icons-react';
import { LEAGUE_STATUS, LEAGUE_FORMATS } from '../../data/leagueSports';
import { getLeagueMatches } from '../../services/leagueService';
import BracketView from './BracketView';

const OverviewTab = ({ league, members, isAdmin, onUpdate }) => {
  const [matches, setMatches] = useState([]);
  const [standings, setStandings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadOverviewData();
  }, [league.id, members]);

  const loadOverviewData = async () => {
    try {
      setLoading(true);
      const matchesData = await getLeagueMatches(league.id);
      setMatches(matchesData);

      // Calculate standings
      const standingsMap = new Map();

      members.forEach(member => {
        standingsMap.set(member.userId, {
          participantId: member.userId,
          participantName: `${member.firstName} ${member.lastName}`,
          wins: 0,
          losses: 0,
          matchesPlayed: 0,
          winPercentage: 0,
          pointsFor: 0,
          pointsAgainst: 0
        });
      });

      matchesData
        .filter(match => match.status === 'completed')
        .forEach(match => {
          const p1Stats = standingsMap.get(match.participant1Id);
          const p2Stats = standingsMap.get(match.participant2Id);

          if (p1Stats && p2Stats) {
            p1Stats.matchesPlayed++;
            p2Stats.matchesPlayed++;
            p1Stats.pointsFor += match.participant1Score || 0;
            p1Stats.pointsAgainst += match.participant2Score || 0;
            p2Stats.pointsFor += match.participant2Score || 0;
            p2Stats.pointsAgainst += match.participant1Score || 0;

            if (match.winnerId === match.participant1Id) {
              p1Stats.wins++;
              p2Stats.losses++;
            } else if (match.winnerId === match.participant2Id) {
              p2Stats.wins++;
              p1Stats.losses++;
            }

            p1Stats.winPercentage = p1Stats.matchesPlayed > 0
              ? (p1Stats.wins / p1Stats.matchesPlayed) * 100
              : 0;
            p2Stats.winPercentage = p2Stats.matchesPlayed > 0
              ? (p2Stats.wins / p2Stats.matchesPlayed) * 100
              : 0;
          }
        });

      const standingsArray = Array.from(standingsMap.values())
        .sort((a, b) => {
          if (b.wins !== a.wins) return b.wins - a.wins;
          if (b.winPercentage !== a.winPercentage) return b.winPercentage - a.winPercentage;
          const aDiff = a.pointsFor - a.pointsAgainst;
          const bDiff = b.pointsFor - b.pointsAgainst;
          return bDiff - aDiff;
        })
        .map((standing, index) => ({ ...standing, rank: index + 1 }));

      setStandings(standingsArray);
    } catch (error) {
      console.error('Error loading overview data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Not set';
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatMatchDateTime = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return null;
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getStatusInfo = (status) => {
    const statusMap = {
      [LEAGUE_STATUS.DRAFT]: { label: 'Draft', className: 'ls-status-draft' },
      [LEAGUE_STATUS.REGISTRATION]: { label: 'Registration Open', className: 'ls-status-registration' },
      [LEAGUE_STATUS.IN_PROGRESS]: { label: 'In Progress', className: 'ls-status-in-progress' },
      [LEAGUE_STATUS.COMPLETED]: { label: 'Completed', className: 'ls-status-completed' },
      [LEAGUE_STATUS.CANCELLED]: { label: 'Cancelled', className: 'ls-status-cancelled' }
    };
    return statusMap[status] || statusMap[LEAGUE_STATUS.DRAFT];
  };

  const getFeaturedMatch = () => {
    // Get next upcoming match
    const upcomingMatches = matches
      .filter(m => m.status === 'upcoming' && m.scheduledTime)
      .sort((a, b) => new Date(a.scheduledTime) - new Date(b.scheduledTime));

    if (upcomingMatches.length > 0) {
      return { match: upcomingMatches[0], type: 'upcoming' };
    }

    // Get most recent completed match
    const completedMatches = matches
      .filter(m => m.status === 'completed')
      .sort((a, b) => new Date(b.scheduledTime || 0) - new Date(a.scheduledTime || 0));

    if (completedMatches.length > 0) {
      return { match: completedMatches[0], type: 'completed' };
    }

    return null;
  };

  const getLeagueStats = () => {
    const totalMatches = matches.length;
    const completedMatches = matches.filter(m => m.status === 'completed').length;
    const upcomingMatches = matches.filter(m => m.status === 'upcoming').length;
    const totalPointsScored = matches
      .filter(m => m.status === 'completed')
      .reduce((sum, m) => sum + (m.participant1Score || 0) + (m.participant2Score || 0), 0);

    return {
      totalMatches,
      completedMatches,
      upcomingMatches,
      totalPointsScored,
      avgPointsPerMatch: completedMatches > 0 ? Math.round(totalPointsScored / completedMatches) : 0
    };
  };

  const statusInfo = getStatusInfo(league.status);
  const formatInfo = LEAGUE_FORMATS[league.format?.toUpperCase().replace('-', '_')] || LEAGUE_FORMATS.CUSTOM;
  const featuredMatchData = getFeaturedMatch();
  const stats = getLeagueStats();
  const topStandings = standings.filter(s => s.matchesPlayed > 0).slice(0, 5);

  // Check if league format should show bracket
  const isTournamentFormat = ['single-elimination', 'double-elimination', 'swiss'].includes(league.format);
  const shouldShowBracket = isTournamentFormat && matches.length > 0;

  return (
    <div className="ls-overview-tab-v2">
      {/* Hero Section */}
      <div className="ls-overview-hero">
        <div className="ls-hero-content">
          <div className="ls-hero-title-row">
            <h1 className="ls-hero-title">{league.name}</h1>
            <span className={`ls-status-badge ${statusInfo.className}`}>
              {statusInfo.label}
            </span>
          </div>
          <div className="ls-hero-meta">
            <div className="ls-meta-item">
              <IconCalendar size={18} />
              <span>{formatDate(league.startDate)} - {formatDate(league.endDate)}</span>
            </div>
            {league.location && (
              <div className="ls-meta-item">
                <IconMapPin size={18} />
                <span>{league.location}</span>
              </div>
            )}
            <div className="ls-meta-item">
              <IconTrophy size={18} />
              <span>{formatInfo.name}</span>
            </div>
          </div>
        </div>
        <div className="ls-hero-participants">
          <div className="ls-participants-number">{members.length}</div>
          <div className="ls-participants-label">Participants</div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="ls-overview-content-grid">
        {/* Featured Match */}
        {featuredMatchData && (
          <div className="ls-featured-match-card">
            <div className="ls-featured-match-header">
              <h3>
                {featuredMatchData.type === 'upcoming' ? 'Next Match' : 'Latest Result'}
              </h3>
              {formatMatchDateTime(featuredMatchData.match.scheduledTime) && (
                <span className="ls-match-date">
                  {formatMatchDateTime(featuredMatchData.match.scheduledTime)}
                </span>
              )}
            </div>
            <div className="ls-featured-match-body">
              <div className={`ls-match-participant ${featuredMatchData.match.winnerId === featuredMatchData.match.participant1Id ? 'ls-winner' : ''}`}>
                <div className="ls-participant-info">
                  {featuredMatchData.match.winnerId === featuredMatchData.match.participant1Id && (
                    <IconCrown size={20} className="ls-winner-crown" />
                  )}
                  <span className="ls-participant-name">{featuredMatchData.match.participant1Name || 'TBD'}</span>
                </div>
                <div className="ls-participant-score">
                  {featuredMatchData.type === 'completed' ? featuredMatchData.match.participant1Score || 0 : '-'}
                </div>
              </div>
              <div className="ls-match-vs">VS</div>
              <div className={`ls-match-participant ${featuredMatchData.match.winnerId === featuredMatchData.match.participant2Id ? 'ls-winner' : ''}`}>
                <div className="ls-participant-info">
                  {featuredMatchData.match.winnerId === featuredMatchData.match.participant2Id && (
                    <IconCrown size={20} className="ls-winner-crown" />
                  )}
                  <span className="ls-participant-name">{featuredMatchData.match.participant2Name || 'TBD'}</span>
                </div>
                <div className="ls-participant-score">
                  {featuredMatchData.type === 'completed' ? featuredMatchData.match.participant2Score || 0 : '-'}
                </div>
              </div>
            </div>
            {featuredMatchData.match.location && (
              <div className="ls-featured-match-footer">
                <IconMapPin size={16} />
                <span>{featuredMatchData.match.location}</span>
              </div>
            )}
          </div>
        )}

        {/* Standings Preview */}
        {topStandings.length > 0 && (
          <div className="ls-standings-preview-card">
            <div className="ls-standings-preview-header">
              <h3>Standings</h3>
              <span className="ls-standings-count">Top {topStandings.length}</span>
            </div>
            <div className="ls-standings-preview-list">
              {topStandings.map((standing, index) => (
                <div key={standing.participantId} className={`ls-standing-item ls-rank-${standing.rank}`}>
                  <div className="ls-standing-rank">
                    {standing.rank === 1 && <IconTrophy size={20} className="ls-rank-icon ls-gold" />}
                    {standing.rank === 2 && <IconMedal size={20} className="ls-rank-icon ls-silver" />}
                    {standing.rank === 3 && <IconMedal size={20} className="ls-rank-icon ls-bronze" />}
                    <span className="ls-rank-number">{standing.rank}</span>
                  </div>
                  <div className="ls-standing-name">{standing.participantName}</div>
                  <div className="ls-standing-stats">
                    <span className="ls-stat-wins">{standing.wins}W</span>
                    <span className="ls-stat-separator">-</span>
                    <span className="ls-stat-losses">{standing.losses}L</span>
                  </div>
                  <div className="ls-standing-percentage">
                    {standing.winPercentage.toFixed(0)}%
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* League Stats */}
        {stats.totalMatches > 0 && (
          <div className="ls-league-stats-card">
            <div className="ls-stats-header">
              <IconChartBar size={24} />
              <h3>League Stats</h3>
            </div>
            <div className="ls-stats-grid">
              <div className="ls-stat-item">
                <div className="ls-stat-value">{stats.totalMatches}</div>
                <div className="ls-stat-label">Total Matches</div>
              </div>
              <div className="ls-stat-item">
                <div className="ls-stat-value">{stats.completedMatches}</div>
                <div className="ls-stat-label">Completed</div>
              </div>
              <div className="ls-stat-item">
                <div className="ls-stat-value">{stats.upcomingMatches}</div>
                <div className="ls-stat-label">Upcoming</div>
              </div>
              <div className="ls-stat-item">
                <div className="ls-stat-value">{stats.avgPointsPerMatch}</div>
                <div className="ls-stat-label">Avg Points/Match</div>
              </div>
            </div>
          </div>
        )}

        {/* Quick Info */}
        <div className="ls-quick-info-card">
          <h3>Quick Info</h3>
          <div className="ls-quick-info-list">
            <div className="ls-info-item">
              <span className="ls-info-label">Format</span>
              <span className="ls-info-value">{formatInfo.name}</span>
            </div>
            <div className="ls-info-item">
              <span className="ls-info-label">Start Date</span>
              <span className="ls-info-value">{formatDate(league.startDate)}</span>
            </div>
            <div className="ls-info-item">
              <span className="ls-info-label">End Date</span>
              <span className="ls-info-value">{formatDate(league.endDate)}</span>
            </div>
            <div className="ls-info-item">
              <span className="ls-info-label">Participants</span>
              <span className="ls-info-value">{members.length} / {league.maxParticipants || '∞'}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tournament Bracket */}
      {shouldShowBracket && (
        <div className="ls-overview-bracket-section">
          <div className="ls-bracket-section-header">
            <IconTrophy size={28} className="ls-bracket-header-icon" />
            <h2>Tournament Bracket</h2>
          </div>
          <BracketView matches={matches} format={league.format} />
        </div>
      )}

      {/* Description and Rules */}
      {(league.description || league.rules) && (
        <div className="ls-overview-details">
          {league.description && (
            <div className="ls-detail-card">
              <h3>About This League</h3>
              <p>{league.description}</p>
            </div>
          )}
          {league.rules && (
            <div className="ls-detail-card">
              <h3>League Rules</h3>
              <p>{league.rules}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OverviewTab;
